<template>
  <div class="user-tab-security">
    <v-card class="mb-7">
      <v-card-title> {{ $t('candidateData') }} </v-card-title>

      <v-card-text>
        <v-form ref="dataForm" v-model="valid" @submit.prevent="handleFormSubmit">
          <!-- Tags -->
          <v-row>
            <v-col cols="12">
              <v-combobox
                v-model="localTags"
                :items="tagsChoices"
                :label="$t('tag')"
                :placeholder="$t('tag')"
                item-text="tag"
                item-value="id"
                multiple
                outlined
                dense
                hide-details
              >
                <template v-slot:selection="{ attrs, item, parent, selected }">
                  <v-chip
                    v-bind="attrs"
                    color="info"
                    outlined
                    :input-value="selected"
                    small
                    label
                    class="font-weight-semibold disable-min-height--chip mt-1"
                  >
                    <span class="pr-3">{{ item === Object(item) ? item.tag : item }}</span>
                    <v-icon x-small @click="parent.selectItem(item)"> $delete </v-icon>
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
          </v-row>

          <!-- Custom fields -->
          <v-row v-if="localCustomFields.length">
            <v-col cols="12">
              <v-divider class="mb-4" />
              <h3 class="text-lg font-weight-semibold mb-3">{{ $t('customFields') }}</h3>
            </v-col>
            <v-col
              v-for="(customField, index) in localCustomFields"
              :key="`CustomField-${customField.id}-${index}`"
              cols="12"
            >
              <custom-field-input :field="customField" />
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-btn
                color="primary"
                class="me-3"
                type="submit"
                :loading="loading"
                :disabled="!areDataChanged"
              >
                {{ $t('save') }}
              </v-btn>
              <v-btn outlined color="secondary" :disabled="!areDataChanged" @click="restoreDefaults">
                {{ $t('discard') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import _ from 'lodash'
import { getCustomFieldsForCandidate } from '@/utils/customFieldUtils'
import employeeUtils from '@/utils/employeeUtils'
import CustomFieldInput from '@/views/_shared/CustomFieldInput.vue'

export default {
  components: {
    CustomFieldInput,
  },
  setup() {
    const { updateCandidateEntryDetails } = useActions('candidates', ['updateCandidateEntryDetails'])
    const { candidateEntry } = useState('candidates', ['candidateEntry'])

    const loading = ref(false)
    const valid = ref(true)
    const dataForm = ref(null)
    const localCustomFields = ref([])
    const localTags = ref([])

    const {
      fetchTagChoices,
      tagsChoices,
    } = employeeUtils()

    const syncLocalState = () => {
      localTags.value = _.cloneDeep(candidateEntry.value.tags || [])
      localCustomFields.value = _.cloneDeep(
        getCustomFieldsForCandidate(candidateEntry.value.customFields || []),
      )
    }

    syncLocalState()

    watch(
      () => candidateEntry.value,
      () => {
        syncLocalState()
      },
      { deep: true },
    )

    const handleFormSubmit = async () => {
      loading.value = true
      await updateCandidateEntryDetails({
        id: candidateEntry.value.id,
        payload: {
          tags: localTags.value.map((tag) => (_.isPlainObject(tag) ? tag : { tag })),
          customFieldsToWrite: localCustomFields.value,
        },
      })
      await fetchTagChoices()
      loading.value = false
    }

    const areDataChanged = computed(() => {
      const tagsChanged =
        !_.isEmpty(_.differenceWith(localTags.value, candidateEntry.value.tags, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(candidateEntry.value.tags, localTags.value, _.isEqual))

      const candidateCustomFields = getCustomFieldsForCandidate(candidateEntry.value.customFields || [])
      const customFieldsChanged =
        !_.isEmpty(_.differenceWith(localCustomFields.value, candidateCustomFields, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(candidateCustomFields, localCustomFields.value, _.isEqual))

      return tagsChanged || customFieldsChanged
    })

    const restoreDefaults = () => {
      syncLocalState()
    }

    onMounted(async () => {
      await fetchTagChoices()
    })

    return {
      handleFormSubmit,
      restoreDefaults,

      valid,
      loading,
      dataForm,
      localTags,
      localCustomFields,
      areDataChanged,
      tagsChoices,
    }
  },
}
</script>
